import React, { useState, useEffect, useCallback } from "react";
import { useAtom } from "jotai";
import { motion } from "framer-motion";
import { themeAtom } from "../../../../../-Atoms";
import { db, doc, writeBatch } from "../../../../../firebaseConfig";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import IconButton from "@mui/material/IconButton";
import RemoveIcon from "@mui/icons-material/Remove";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { MobileDatePicker } from "@mui/x-date-pickers/MobileDatePicker";
import debounce from "lodash.debounce";
import dayjs from "dayjs";

import UniversalTextField from "../../../../../GlobalComponents/UniversalTextField";

const VideoEditingItemV2 = ({
  item,
  items,
  setItems,
  index,
  showRemoveVideoEditingItem,
  jobData,
  staffData,
  findStaff,
}) => {
  const [theme] = useAtom(themeAtom);
  const [date, setDate] = useState("");
  const [hours, setHours] = useState("");
  const [staff, setStaff] = useState("");
  const [notes, setNotes] = useState("");

  useEffect(() => {
    if (date !== item.date) setDate(item.date);
    if (hours !== item.hours) setHours(item.hours);
    if (staff !== item.staff) setStaff(item.staff);
    if (notes !== item.notes) setNotes(item.notes);
  }, [item, date, hours, staff, notes]);

  // eslint-disable-next-line
  const updateFirestore = useCallback(
    debounce((updatedItems) => {
      const batch = writeBatch(db);
      const docRef = doc(db, "JobData", jobData.id);
      batch.update(docRef, { videoEditingBreakdown: updatedItems });
      batch.commit().catch((e) => console.log(e));
    }, 500),
    [jobData.id]
  );

  const removeVideoEditingItem = () => {
    const updatedItems = items.filter((_, i) => i !== index);
    setItems(updatedItems);
    updateFirestore(updatedItems);
  };

  const handleChange = (field, value) => {
    const updatedItems = [...items];
    updatedItems[index] = { ...item, [field]: value };
    setItems(updatedItems);
    updateFirestore(updatedItems);
  };

  // Find the selected staff object for Autocomplete value
  const selectedStaff = staffData.find((s) => s.id === staff) || null;

  const itemVariants = {
    hidden: { opacity: 0, x: -10 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.2,
        ease: [0.25, 0.1, 0.25, 1.0],
      },
    },
  };

  return (
    <motion.div variants={itemVariants}>
      <Box sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        p: 2,
        borderRadius: "8px",
        background: theme === "dark" 
          ? "rgba(45,45,45,0.3)" 
          : "rgba(240,240,240,0.3)",
        border: `1px solid ${
          theme === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)"
        }`,
        position: "relative"
      }}>
        {/* First Row: Staff, Hours, Date */}
        <Box sx={{
          display: "flex",
          gap: 2,
          flexWrap: "wrap",
          alignItems: "flex-start"
        }}>
          <Autocomplete
            disablePortal
            sx={{ 
              flex: "1 1 30%", 
              minWidth: "150px",
              "& .MuiFilledInput-root": {
                borderRadius: "8px",
                backgroundColor: theme === "dark" ? "rgba(45,45,45,0.7)" : "rgba(250,250,250,0.9)",
                transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                "&:hover": {
                  backgroundColor: theme === "dark" ? "rgba(55,55,55,0.8)" : "rgba(255,255,255,1)",
                },
                "&.Mui-focused": {
                  backgroundColor: theme === "dark" ? "rgba(60,60,60,0.9)" : "rgba(255,255,255,1)",
                  boxShadow: theme === "dark"
                    ? "0 0 0 2px rgba(100,100,255,0.5)"
                    : "0 0 0 2px rgba(100,100,255,0.3)",
                },
              },
              "& .MuiInputLabel-root": {
                color: theme === "dark" ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.7)",
                fontWeight: 500,
                fontSize: "15px",
                transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
              },
            }}
            options={staffData}
            getOptionLabel={(option) => option.firstName ? `${option.firstName} ${option.lastName}` : ""}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="filled"
                fullWidth
                inputProps={{ ...params.inputProps, style: { fontSize: 15 } }}
                InputLabelProps={{
                  ...params.InputLabelProps,
                  style: { fontSize: 15 },
                }}
                InputProps={{
                  ...params.InputProps,
                  disableUnderline: true,
                }}
                size="small"
                label="Editor"
              />
            )}
            value={selectedStaff}
            onChange={(e, value) => {
              const val = value?.id || "";
              setStaff(val);
              handleChange("staff", val);
            }}
          />
          
          <UniversalTextField
            value={hours}
            label="Hours"
            type="number"
            inputProps={{ step: "0.1", min: "0" }}
            onCallback={(newValue) => {
              // Only allow numeric values and decimals
              const numericValue = newValue.replace(/[^0-9.]/g, "");
              setHours(numericValue);
              handleChange("hours", numericValue);
              return numericValue;
            }}
            size="small"
            sx={{ 
              flex: "1 1 30%",
              minWidth: "100px",
              marginTop: 0,
              "& .MuiInputBase-root": {
                fontSize: "15px"
              },
              "& .MuiInputLabel-root": {
                fontSize: "15px"
              },
            }}
            noPadding
          />
          
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <MobileDatePicker
              label="Date"
              value={date ? dayjs(date) : null}
              onChange={(d) => {
                if (!d || !d.isValid()) return;
                const dateValue = d.toISOString();
                setDate(dateValue);
                handleChange("date", dateValue);
              }}
              format="DD/MM/YYYY"
              sx={{ 
                flex: "1 1 30%", 
                minWidth: "150px",
                "& .MuiFilledInput-root": {
                  borderRadius: "8px",
                  backgroundColor: theme === "dark" ? "rgba(45,45,45,0.7)" : "rgba(250,250,250,0.9)",
                  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                  "&:hover": {
                    backgroundColor: theme === "dark" ? "rgba(55,55,55,0.8)" : "rgba(255,255,255,1)",
                  },
                  "&.Mui-focused": {
                    backgroundColor: theme === "dark" ? "rgba(60,60,60,0.9)" : "rgba(255,255,255,1)",
                    boxShadow: theme === "dark"
                      ? "0 0 0 2px rgba(100,100,255,0.5)"
                      : "0 0 0 2px rgba(100,100,255,0.3)",
                  },
                },
                "& .MuiInputLabel-root": {
                  color: theme === "dark" ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.7)",
                  fontWeight: 500,
                  fontSize: "15px",
                  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                },
              }}
              slotProps={{
                textField: {
                  variant: "filled",
                  inputProps: {
                    style: { fontSize: 15 },
                  },
                  InputLabelProps: {
                    style: { fontSize: 15 },
                  },
                  InputProps: {
                    disableUnderline: true,
                  },
                  size: "small",
                },
              }}
            />
          </LocalizationProvider>
        </Box>
        
        {/* Second Row: Notes */}
        <UniversalTextField
          value={notes}
          label="Notes"
          multiline
          rows={2}
          onCallback={(newValue) => {
            setNotes(newValue);
            handleChange("notes", newValue);
          }}
          sx={{ 
            marginTop: 0,
            "& .MuiInputBase-root": {
              fontSize: "15px"
            },
            "& .MuiInputLabel-root": {
              fontSize: "15px"
            }
          }}
          noPadding
        />
        
        {/* Remove Button */}
        <IconButton
          sx={{ 
            display: showRemoveVideoEditingItem ? "block" : "none",
            position: "absolute",
            top: 8,
            right: 8,
            color: theme === "dark" ? "rgba(255,100,100,0.8)" : "rgba(200,50,50,0.8)",
            width: "32px",
            height: "32px"
          }}
          onClick={removeVideoEditingItem}
        >
          <RemoveIcon fontSize="small" />
        </IconButton>
      </Box>
    </motion.div>
  );
};

export default VideoEditingItemV2;